import { Link } from "react-router-dom";
import { HOTEL } from "@/data/site";
import { Logo } from "@/components/Logo";
import { BookDirect, CallBtn, DirectionsBtn } from "@/components/CTAButtons";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

const SECTIONS = [
  {
    title: "Stay",
    links: [
      { label: "Rooms & Rates", to: "/rooms" },
      { label: "Amenities", to: "/amenities" },
      { label: "Indoor Pool", to: "/indoor-pool" },
      { label: "Truck Parking", to: "/truck-parking" },
      { label: "Extended Stay", to: "/extended-stay" },
    ],
  },
  {
    title: "Travelers",
    links: [
      { label: "Business Travel", to: "/business-travel" },
      { label: "Family Travel", to: "/family-travel" },
      { label: "Group Bookings", to: "/group-bookings" },
      { label: "Local Attractions", to: "/attractions" },
      { label: "Nearby Cities", to: "/areas" },
    ],
  },
  {
    title: "Explore",
    links: [
      { label: "Travel Blog", to: "/blog" },
      { label: "FAQ", to: "/faq" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
];

export const Footer = () => (
  <footer className="border-t border-charcoal/10 bg-bone pb-24 pt-0 md:pb-0" data-testid="footer">
    {/* Yellow CTA band */}
    <div className="bg-brand-yellow">
      <div className="mx-auto flex max-w-[1400px] flex-col justify-between gap-8 px-5 py-16 md:flex-row md:items-end md:px-10 md:py-20">
        <div className="max-w-xl">
          <p className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-brand-red">Book Direct · Save More</p>
          <h2 className="mt-3 font-display text-5xl uppercase leading-[0.9] tracking-tight text-charcoal md:text-7xl">
            Your Ottawa,<br />Kansas stay awaits.
          </h2>
        </div>
        <div className="flex flex-wrap gap-3">
          <BookDirect testid="footer-book-direct" />
          <CallBtn testid="footer-call" />
          <DirectionsBtn testid="footer-directions" />
        </div>
      </div>
    </div>

    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid gap-8 border-b border-charcoal/10 py-14 md:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-5 max-w-xs font-body text-sm leading-relaxed text-fog">
            The independent Super 8 by Wyndham right off I-35 in Ottawa, Kansas — free breakfast,
            free WiFi, indoor pool and large-vehicle parking.
          </p>
          <div className="mt-6 space-y-3">
            <a href={HOTEL.directionsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-sm text-charcoal/80 transition-colors hover:text-brand-red">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {HOTEL.fullAddress}
            </a>
            <a href={`tel:${HOTEL.phone}`} className="flex items-center gap-3 text-sm text-charcoal/80 transition-colors hover:text-brand-red">
              <Phone className="h-4 w-4 shrink-0 text-brand-red" /> {HOTEL.phoneDisplay}
            </a>
            <a href={`mailto:${HOTEL.email}`} className="flex items-center gap-3 text-sm text-charcoal/80 transition-colors hover:text-brand-red">
              <Mail className="h-4 w-4 shrink-0 text-brand-red" /> {HOTEL.email}
            </a>
            <p className="flex items-center gap-3 text-sm text-charcoal/80">
              <Clock className="h-4 w-4 shrink-0 text-brand-red" /> 24-Hour Front Desk
            </p>
          </div>
        </div>
        {SECTIONS.map((s) => (
          <div key={s.title}>
            <p className="font-heading text-sm font-black uppercase tracking-wide text-brand-red">{s.title}</p>
            <ul className="mt-4 space-y-2.5">
              {s.links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace("/", "")}`}
                    className="font-body text-sm text-charcoal/70 transition-colors hover:text-brand-red"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex flex-col items-start justify-between gap-4 py-8 text-xs text-fog md:flex-row md:items-center">
        <p>© {new Date().getFullYear()} {HOTEL.name}. All rights reserved.</p>
        <p>{HOTEL.fullAddress} · Independently owned & operated under the Super 8 by Wyndham brand.</p>
      </div>
    </div>
  </footer>
);
